import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnimatedCloseButtonProps } from './types';

/**
 * AnimatedCloseButton component
 * 
 * A beautiful and modern close button with smooth Framer Motion animations.
 * Features hover effects, scale animations, and color transitions.
 */
export function AnimatedCloseButton({
  onClick,
  className,
  size = 40,
  variant = 'default',
  disabled = false,
  'data-test-id': testId = 'animated-close-button',
}: AnimatedCloseButtonProps) {
  // Define variant styles
  const variantStyles = {
    default: 'bg-white/10 hover:bg-white/20 text-gray-700 dark:text-gray-300',
    primary: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400',
    ghost: 'bg-transparent hover:bg-gray-100/20 text-gray-500 dark:text-gray-400'
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      data-test-id={testId}
      className={cn(
        // Base styles
        "relative flex items-center justify-center rounded-full",
        "backdrop-blur-sm border border-transparent",
        "transition-colors duration-200",
        // Hover and focus states
        "hover:border-gray-200/30 dark:hover:border-gray-700/30",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50",
        // Disabled state
        disabled && "opacity-50 cursor-not-allowed",
        // Apply variant styles
        variantStyles[variant],
        className
      )}
      style={{
        width: size,
        height: size
      }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 25
      }}
    >
      {/* X icon with its own animation */}
      <motion.div
        whileHover={{ rotate: 90 }}
        transition={{ duration: 0.2 }}
      >
        <X 
          size={size * 0.5} 
          strokeWidth={2.5}
          className="transition-transform duration-200"
        />
      </motion.div>
      
      {/* Animated ring effect on hover */}
      <motion.div
        className="absolute inset-0 rounded-full"
        initial={{ opacity: 0 }}
        whileHover={{ 
          opacity: 1,
          scale: 1.05,
        }}
        transition={{ duration: 0.2 }}
      >
        <div className="absolute inset-0 rounded-full bg-current opacity-5" />
      </motion.div>
    </motion.button>
  );
}
